import React, { forwardRef } from 'react';
import type {
    ActionMeta,
    GroupBase,
    MenuPosition,
    StylesConfig,
    SelectComponentsConfig,
} from 'react-select';
import Select from 'react-select/base';

// components
import {
    GroupedOption,
    SearchableSelectType,
} from '../../../components/inputs/searchableSelect';
import AampeCreateSelectDropdown from '../../../components/shared/AampeCreateSelectDropdown/AampeCreateSelectDropdown';
import CustomClearIndicator from '../../../components/shared/CustomClearIndicator';

import { DangerousCharacterErrorMessage } from '../../../constants/label.constants';

import { isSafeString } from '../../../helpers/labelHelpers';

import { VariantWithLabel } from 'models/variant.model';

import { toastService } from '../../../services/toast.service';

import AlternateLabelDropdownOption from './AlternateLabelDropdownOption';

interface LabelsCreateAndSelectDropdownProps {
    options: SearchableSelectType[] | GroupedOption[];
    name: string;
    onChange: (
        field: string,
        value: SearchableSelectType | SearchableSelectType[],
        actionType?: ActionMeta<SearchableSelectType>,
    ) => void;
    onBlur?: (field: string, isTouched?: boolean) => void;
    value?: SearchableSelectType | SearchableSelectType[];
    placeholder?: string;
    ref?: any;
    data?: VariantWithLabel;
    isMultiple?: boolean;
    isDisabled?: boolean;
    isEditMenuOpen?: boolean;
    isCreatable?: boolean;
    components?: Partial<
        SelectComponentsConfig<
            SearchableSelectType,
            boolean,
            GroupBase<SearchableSelectType>
        >
    >;
    isClearable?: boolean;
    menuPortalTarget?: HTMLElement | null;
    className?: string;
    customStyle?: StylesConfig<
        SearchableSelectType,
        boolean,
        GroupBase<SearchableSelectType>
    >;
    footer?: React.ReactNode;
    sortOption?: boolean;
    menuPosition?: MenuPosition;
}

const LabelsCreateAndSelectDropdown: React.FC<
    React.PropsWithChildren<LabelsCreateAndSelectDropdownProps>
> = forwardRef<
    Select<SearchableSelectType, boolean, GroupBase<SearchableSelectType>>,
    LabelsCreateAndSelectDropdownProps
>(
    (
        {
            options,
            name,
            onChange,
            onBlur,
            value,
            placeholder,
            data,
            isMultiple,
            isDisabled,
            isEditMenuOpen,
            isCreatable,
            components,
            isClearable,
            menuPortalTarget = document.body,
            className,
            customStyle,
            footer,
            sortOption,
            menuPosition = 'absolute',
        },
        ref,
    ) => {
        const handleChange = (
            field: string,
            value: SearchableSelectType | SearchableSelectType[],
            actionType?: ActionMeta<SearchableSelectType>,
        ) => {
            const selectValue =
                isMultiple && actionType?.action === 'create-option'
                    ? actionType?.option?.value
                    : (value as SearchableSelectType)?.value;
            if (typeof selectValue === 'string' && !isSafeString(selectValue)) {
                toastService.showError(DangerousCharacterErrorMessage('Label'));
                return;
            }

            onChange(field, value, actionType);
        };
        return (
            <>
                <AampeCreateSelectDropdown
                    className={className}
                    ref={ref}
                    isMultiple={isMultiple}
                    value={value}
                    name={name}
                    isSortOption={sortOption}
                    options={options}
                    onChange={handleChange}
                    onBlur={onBlur}
                    isClearable={isClearable}
                    placeholder={placeholder}
                    isDisabled={isDisabled}
                    menuIsOpen={isEditMenuOpen || (data && true)}
                    menuPlacement={'auto'}
                    menuPortalTarget={menuPortalTarget}
                    menuPosition={menuPosition}
                    components={{
                        Option: (innerProps) => (
                            <>
                                <AlternateLabelDropdownOption {...innerProps} />
                            </>
                        ),
                        ClearIndicator: CustomClearIndicator,
                        DropdownIndicator: null,
                        ...components,
                    }}
                    creatable={isCreatable}
                    customStyle={customStyle}
                    footer={footer}
                />
            </>
        );
    },
);

export default LabelsCreateAndSelectDropdown;
